import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from "@angular/core";
import { MeiliQuery } from "../../index";

import { CarRentalService } from "../../services/car-rental.service";
import { SsrHelperService } from "../../../../core/services/ssr-helper.service";

// DOCS: https://meili.atlassian.net/servicedesk/customer/portal/1/article/1236631553
// You can access the detailed documentation using your corporate email and password.
// This will allow you to read and understand how to work with the Meili service.

/**
 * Basic usage of CarRentalComponent:
 *
 * 1. Import the component into your Angular module or another component:
 *    import { CarRentalComponent } from './car-rental.component';
 *
 * 2. Use the component in your template with appropriate inputs:
 *
 *    <app-car-rental
 *      [currencyCode]="'USD'"
 *      [query]="yourQueryObject">
 *    </app-car-rental>
 *
 * 3. The `query` input expects an object of type `MeiliQuery`.
 *    Example:
 *
 *    const yourQueryObject: MeiliQuery = {
 *      pickupLocation: { latitude: 49.8397, longitude: 24.0297, name: 'Lviv' },
 *      dropoffLocation: { latitude: 51.5074, longitude: -0.1278, name: 'London' },
 *      pickupDate: '2024-11-27',
 *      pickupTime: '10:00',
 *      dropoffDate: '2024-12-08',
 *      dropoffTime: '15:00',
 *      driverAge: 30,
 *      residency: 'UA',
 *      numberOfPassengers: 5,
 *    };
 *
 * 4. The component automatically initializes the Meili rental service after view initialization.
 *    If any input changes (like currencyCode or query), the service will be reinitialized.
 */
@Component({
  selector: 'app-car-rental-preview',
  templateUrl: './car-rental-preview.component.html',
  styleUrls: ['./car-rental-preview.component.scss'],
  providers: [CarRentalService],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CarRentalPreviewComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() currencyCode: string = 'USD';
  @Input() query: MeiliQuery | null = null;

  public meiliSecretKey: string;

  get userLocale(): string {
    return navigator.languages?.[0] || navigator.language || 'en-US';
  }

  get dataQuery(): string {
    const jsonString = JSON.stringify([this.query]);
    return btoa(unescape(encodeURIComponent(jsonString)));
  }

  constructor(
    private carRentalService: CarRentalService,
    private ssrHelperService: SsrHelperService
  ) {
    this.meiliSecretKey = carRentalService.meiliSecretKey;
  }

  ngAfterViewInit() {
    if (!this.ssrHelperService.userAgent) return;
    window.addEventListener("MEILI_CONNECT_LISTENER", this.handleMeiliEvent.bind(this));
  }

  ngOnDestroy() {
    window.removeEventListener("MEILI_CONNECT_LISTENER", this.handleMeiliEvent.bind(this))
  }

  ngOnChanges(changes: SimpleChanges) {
    this.updateFrame(changes);
  }

  private updateFrame(changes: SimpleChanges): void {
    let updateFrame = false;
    const keys = ['currencyCode', 'query'];
    for (const key of keys) {
      updateFrame =
        changes[key]?.previousValue !== changes[key]?.currentValue ||
        false ||
        updateFrame;
    }
    if (updateFrame) {
      this.carRentalService.loadDirectCdnScript();
    }
  }

  private handleMeiliEvent(event: any) {
    if (event.detail?.type === "MEILI_EVENT_BOOKING_SUCCESS") {
      const bookingData = event.detail.data;
      this.sendBookingToBackend(bookingData);
    }
  }

  private sendBookingToBackend(bookingData: any) {
    this.carRentalService.notifyBookingSuccess(bookingData).subscribe();
  }
}
